import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { INSIGHTS } from "@/lib/insights";
import { useLang } from "@/lib/LangContext";
import Seo from "@/components/Seo";
import QuoteForm from "@/components/QuoteForm";
import EditorialMarquee from "@/components/EditorialMarquee";

export default function InsightsIndex() {
  const { lang } = useLang();
  const [featured, ...rest] = INSIGHTS;

  return (
    <div data-testid="page-insights">
      <Seo
        title="Security Insights · Field Journal | Maze Security NB"
        description="Field analyses on New Brunswick security: copper theft on construction sites, Atlantic festival crowd safety, cannabis loss prevention, fire-watch compliance, port security."
        path="/insights"
      />
      <section className="pt-32 md:pt-40 pb-16 border-b border-border">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-6">
            [ {lang === "fr" ? "Perspectives · Journal de terrain" : "Insights · The Field Journal"} ]
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}
            className="font-serif font-light text-5xl md:text-9xl leading-[0.9] tracking-tighter"
          >
            {lang === "fr" ? "Perspectives sécurité " : "Security insights "}
            <i className="text-primary">{lang === "fr" ? "du terrain NB." : "from the NB field."}</i>
          </motion.h1>
          <p className="text-foreground/80 mt-8 max-w-2xl text-lg leading-relaxed">
            {lang === "fr"
              ? "Analyses de terrain, notes de conformité et reportages sur ce que la sécurité au Nouveau-Brunswick exige réellement — écrits par les gens qui font le travail."
              : "Field analyses, compliance notes and reporting on what security in New Brunswick actually demands — written by the people who do the work."}
          </p>
        </div>
      </section>

      {/* Featured */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 py-20 md:py-24">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-6">
            [ Featured ]
          </div>
          <Link
            to={`/insights/${featured.slug}`}
            data-testid={`insight-card-${featured.slug}`}
            className="group grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 border-t border-border pt-10"
          >
            <div className="md:col-span-7 aspect-[16/10] overflow-hidden border border-border relative">
              <img
                src={featured.hero}
                alt=""
                className="absolute inset-0 w-full h-full object-cover grayscale contrast-125 group-hover:scale-105 group-hover:grayscale-0 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/70 via-transparent to-transparent" />
              <div className="absolute top-6 left-6 font-mono text-[10px] tracking-[0.3em] uppercase text-white/80">
                {featured.kicker}
              </div>
            </div>
            <div className="md:col-span-5 flex flex-col justify-center">
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-4 flex items-center gap-4">
                <span>{featured.date}</span>
                <span>·</span>
                <span>{featured.read}</span>
              </div>
              <h2 className="font-serif font-light text-3xl md:text-5xl leading-[1.05] tracking-tight mb-6 group-hover:text-primary transition-colors">
                {featured.title}
              </h2>
              <p className="text-foreground/70 leading-relaxed mb-6">{featured.excerpt}</p>
              <span className="font-mono text-xs tracking-[0.2em] uppercase inline-flex items-center gap-2 text-primary">
                {lang === "fr" ? "Lire l'article" : "Read Article"} <ArrowUpRight size={14} />
              </span>
            </div>
          </Link>
        </div>
      </section>

      {/* Grid of remaining */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 py-20">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-6">
            [ {lang === "fr" ? "Tous les articles" : "All Articles"} · {INSIGHTS.length} ]
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 border-t border-l border-border">
            {rest.map((a, i) => (
              <motion.div
                key={a.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease: [0.76, 0, 0.24, 1] }}
                className="border-r border-b border-border"
              >
                <Link
                  to={`/insights/${a.slug}`}
                  data-testid={`insight-card-${a.slug}`}
                  className="block p-8 h-full group"
                >
                  <div className="aspect-[16/10] overflow-hidden border border-border mb-6 relative">
                    <img
                      src={a.hero}
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover grayscale contrast-125 group-hover:scale-105 group-hover:grayscale-0 transition-all duration-700"
                    />
                  </div>
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-3">
                    {a.tag} · {a.read}
                  </div>
                  <h3 className="font-serif text-2xl md:text-3xl leading-tight tracking-tight mb-3 group-hover:text-primary transition-colors">
                    {a.title}
                  </h3>
                  <p className="text-foreground/70 leading-relaxed text-sm">{a.excerpt}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <EditorialMarquee />
      <QuoteForm />
    </div>
  );
}
